import { useState, useRef, useEffect } from 'react';
import { AppPage, AppCard } from '../../components/general';
import { Toast } from 'primereact/toast';
import { DataTable, TableDefinition, DataTableSearchCreateHeader } from '../../components/datatable';
import { ProgressBar } from 'primereact/progressbar';
import { BlockUI } from 'primereact/blockui';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { ProgressSpinner } from 'primereact/progressspinner';
import WebhookSubscriptionService from '../../services/webhookSubscriptionService';
import { EditWebhook } from './EditWebHook';
import { WebhookSubscription } from '../../models/webhookSubscriptions';

export const WebhookSubscriptions = (): JSX.Element => {
  const toast = useRef<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [webhooks, setWebhooks] = useState<WebhookSubscription[]>([]);
  const [search, setSearch] = useState<string>('');
  const [editorSidebarVisible, setEditorSidebarVisible] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(window.innerWidth < 768);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookSubscription | null>(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
  const [confirmationData, setConfirmationData] = useState<WebhookSubscription | null>(null);
  const [confirmationOperation, setConfirmationOperation] = useState<'save' | 'delete'>('save');

  const webhookService = WebhookSubscriptionService.getInstance();

  useEffect(() => {
    fetchWebhooks();
  }, []);

  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchWebhooks = (): void => {
    setIsLoading(true);
    webhookService
      .getAll()
      .then((result) => {
        setWebhooks(result ?? []);
      })
      .catch((error) => {
        showError(error.message);
        setWebhooks([]);
      })
      .finally(() => setIsLoading(false));
  };

  const showMessage = (
    severity: 'success' | 'info' | 'warn' | 'error',
    summary: string,
    detail: string,
    life: number = 3000
  ) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life,
    });
  };

  const showError = (message: string) => {
    showMessage('error', 'Error', message, 5000);
  };

  const onEdit = (webhook: WebhookSubscription): void => {
    setSelectedWebhook(webhook);
    setEditorSidebarVisible(true);
  };

  const onDelete = (webhook: WebhookSubscription) => {
    setSelectedWebhook(webhook);
    setConfirmationOperation('delete');
    setConfirmationData(webhook);
    setConfirmDialogVisible(true);
  };

  const handleConfirmAccept = () => {
    setConfirmDialogVisible(false);

    if (confirmationOperation === 'delete' && confirmationData?.webhookSubscriptionId) {
      deleteWebhook(confirmationData.webhookSubscriptionId);
    } else if (confirmationOperation === 'save' && confirmationData) {
      saveSubscription(confirmationData);
    }
  };

  const handleConfirmReject = () => {
    setConfirmDialogVisible(false);
  };

  const deleteWebhook = (id: string) => {
    setIsDeleting(true);
    webhookService
      .delete(id)
      .then(() => {
        showMessage('success', 'Success', `Webhook successfully deleted`);
        fetchWebhooks();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsDeleting(false));
  };

  const saveSubscription = (subscription: WebhookSubscription) => {
    setIsSubmitting(true);
    const isUpdate = !!subscription.webhookSubscriptionId;
    const request = isUpdate
      ? webhookService.update(subscription.webhookSubscriptionId!, subscription)
      : webhookService.create(subscription);

    request
      .then(() => {
        showMessage('success', 'Success', `Subscription successfully ${isUpdate ? 'updated' : 'created'}`);
        setEditorSidebarVisible(false);
        fetchWebhooks();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsSubmitting(false));
  };

  const eventsBodyTemplate = (webhook: WebhookSubscription) => {
    if (!webhook.events?.length) {
      return <span className="text-gray-500">None</span>;
    }
    return <span>{webhook.events.join(', ')}</span>;
  };

  const authTypeBodyTemplate = (webhook: WebhookSubscription) => {
    return <span>{webhook.authenticationSettings?.authenticationType ?? 'None'}</span>;
  };

  const actionBodyTemplate = (webhook: WebhookSubscription) => {
    return (
      <div className="flex">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={() => onEdit(webhook)}
          tooltip="Edit Webhook"
          tooltipOptions={{ position: 'left' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text"
          onClick={() => onDelete(webhook)}
          tooltip="Delete Webhook"
          tooltipOptions={{ position: 'left' }}
          severity="danger"
        />
      </div>
    );
  };

  const tableDef: TableDefinition<WebhookSubscription> = {
    columns: [
      {
        field: 'url',
        header: 'URL',
        dataType: 'text',
      },
      {
        field: 'events',
        header: 'Events',
        dataType: 'text',
        body: eventsBodyTemplate,
      },
      {
        header: 'Authentication',
        dataType: 'text',
        body: authTypeBodyTemplate,
      },
      {
        header: 'Actions',
        body: actionBodyTemplate,
        dataType: 'none',
        sortable: false,
        filter: false,
      },
    ],
  };

  return (
    <AppPage title="Webhook Subscriptions" breadcrumbs="Admin - Webhook Subscriptions">
      <AppCard>
        <Toast ref={toast} />
        <ConfirmDialog />

        <Dialog
          header={confirmationOperation === 'save' ? 'Confirm Changes' : 'Confirm Delete'}
          visible={confirmDialogVisible}
          onHide={handleConfirmReject}
          style={{ width: '450px' }}
          footer={
            <div className="flex justify-content-end gap-2">
              <Button label="No" icon="pi pi-times" onClick={handleConfirmReject} className="p-button-text" />
              <Button label="Yes" icon="pi pi-check" onClick={handleConfirmAccept} autoFocus />
            </div>
          }
        >
          <div className="flex align-items-center gap-3">
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: 'var(--yellow-500)' }} />
            {confirmationOperation === 'save' ? (
              <span>Are you sure you want to save these changes?</span>
            ) : (
              <span>Are you sure you want to delete this webhook?</span>
            )}
          </div>
        </Dialog>

        <Sidebar
          visible={editorSidebarVisible}
          position="right"
          onHide={() => setEditorSidebarVisible(false)}
          className="p-sidebar-md md:p-sidebar-lg"
          style={isSmallScreen ? undefined : { width: '100%', maxWidth: '700px' }}
          header={selectedWebhook ? `Edit Webhook Subscription` : `Add Webhook Subscription`}
          fullScreen={isSmallScreen}
          modal
        >
          <BlockUI blocked={isSubmitting} fullScreen={false} className="relative">
            {isSubmitting && (
              <div
                className="flex align-items-center justify-content-center"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  zIndex: 1,
                }}
              >
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
              </div>
            )}
            <EditWebhook
              subscription={selectedWebhook}
              onSave={(webhook) => {
                setConfirmationData(webhook);
                setConfirmationOperation('save');
                setConfirmDialogVisible(true);
              }}
              onCancel={() => setEditorSidebarVisible(false)}
            />
          </BlockUI>
        </Sidebar>

        {isDeleting && (
          <div className="w-full mb-3">
            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
            <div className="text-center mt-2 text-sm text-primary">Deleting Webhook Subscription...</div>
          </div>
        )}

        <DataTable
          loading={isLoading}
          header={
            <DataTableSearchCreateHeader
              onCreate={() => {
                setSelectedWebhook(null);
                setEditorSidebarVisible(true);
              }}
              onSearchChange={setSearch}
            />
          }
          tableDef={tableDef}
          data={webhooks}
          globalFilterValue={search}
        />
      </AppCard>
    </AppPage>
  );
};
